/**
 * The Playground's API (lumen-be `modules/simulations`, migrations 0117–0120). Every route needs
 * `simulations.run`, and the demo organisation within the caller's reach. See
 * Docs/08-views/PLAYGROUND.md.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export type SimulationState = 'priming' | 'building' | 'live' | 'paused' | 'deleted';

export interface EpisodeView {
  id: number;
  scenario: string;
  label: string;
  source: 'history' | 'live';
  startedAt: string;
  endsAt: string;
}

export interface ActiveEpisode extends EpisodeView {
  phases: Array<{ key: string; label: string; startsAt: string; endsAt: string }>;
  phase: string | null;
}

export interface Simulation {
  id: string;
  name: string;
  preset: string;
  site: { id: string; name: string; slug: string };
  state: SimulationState;
  stateSince: string;
  resumeTo: 'priming' | 'building' | 'live' | null;
  historyFrom: string;
  cursorAt: string | null;
  historyProgress: number | null;
  /** Devices as the setup form counts them. */
  devices: number;
  /** It has the Modbus gateway its first generators and room sensor are read through (not counted). */
  throughGateway: boolean;
  /** Units per counted kind (0120). */
  counts: Record<string, number>;
  /** An edit not yet in force: when its new setup takes over. */
  takesOverAt: string | null;
  /** Devices the last edit removed that are still being cleared. */
  purging: number;
  /** Seconds the generator spaces this site's history payloads at: 2, or more for a big site. */
  historySecondsPerPayload: number;
  lastError: string | null;
  createdAt: string;
  active: ActiveEpisode | null;
  episodes: EpisodeView[];
}

export interface SimulationsResponse {
  demoOrganisation: { id: string; name: string };
  worker: { running: boolean; heartbeatAt: string | null };
  simulations: Simulation[];
}

export interface CatalogueKind {
  key: string;
  label: string;
  blurb: string;
  min: number;
  max: number;
  default: number;
  /** Payload entries one more of these adds, for the history estimate. */
  entries: number;
}

export interface CatalogueScenario {
  key: string;
  label: string;
  group: string;
  blurb: string;
  seconds: number;
  phases: Array<{ key: string; label: string; seconds: number }>;
}

export interface CataloguePreset {
  key: string;
  label: string;
  archetype: string;
  industry: string;
  region: string;
  kinds: CatalogueKind[];
}

export interface Catalogue {
  presets: CataloguePreset[];
  scenarios: CatalogueScenario[];
  historyDays: { min: number; max: number; default: number };
  /** The generator's history pacing and grid, for the time estimate: the 2 s cap, or slower by size. */
  historySecondsPerPayload: number;
  historyEntriesPerSecond: number;
  historyStepSeconds: number;
}

/** A simulated site's site and setup, as the setup form opens on it (`GET /simulations/:id/setup`). */
export interface SimulationSetup {
  id: string;
  preset: string;
  historyDays: number;
  name: string;
  slug: string;
  city: string | null;
  region: string;
  lat: number | null;
  lng: number | null;
  attributes: Record<string, unknown>;
  counts: Record<string, number>;
}

/** The site and its detail as Onboard a site collects them, stored as a real site's are. */
export interface SimulatedSiteDetail {
  name: string;
  slug?: string;
  city?: string;
  region?: string;
  lat?: number;
  lng?: number;
  attributes: Record<string, unknown>;
}

export interface Reading {
  key: string;
  label: string;
  unit: string | null;
  instances: number[];
  normal: number;
  min: number | null;
  max: number | null;
  scale: number;
  offset: number;
}

export interface DeviceReadings {
  kind: string;
  kindLabel: string;
  /** The wire device a step names. */
  device: string;
  /** What an operator calls it: "DG 2 · Cummins PCC3300", "UPS 1". */
  machine: string;
  readings: Reading[];
}

export type StepWith = 'after' | 'previous';

export type Step =
  | {
      kind: 'move';
      device: string;
      key: string;
      instance: number | null;
      to: number;
      rampSeconds: number;
      holdSeconds: number;
      with: StepWith;
    }
  | { kind: 'scenario'; scenario: string; with: StepWith };

export interface SavedScenario {
  id: string;
  name: string;
  definition: { steps: Step[] };
  createdAt: string;
}

const KEY = ['simulations'] as const;

export function useSimulations(enabled: boolean) {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<SimulationsResponse>('/simulations'),
    enabled,
    /* A simulation moves on its own — history building, phases changing — so the screen polls. */
    refetchInterval: 5_000,
  });
}

export function useSimulationCatalogue(enabled: boolean) {
  return useQuery({
    queryKey: ['simulations', 'catalogue'],
    queryFn: () => api.get<Catalogue>('/simulations/catalogue'),
    enabled,
    staleTime: Infinity,
  });
}

/** What the builder may move: one simulated site's devices, copies included, or the catalogue's. */
export function useSimulationReadings(enabled: boolean, simulationId: string | null = null) {
  return useQuery({
    queryKey: ['simulations', 'readings', simulationId],
    queryFn: () =>
      api.get<{ devices: DeviceReadings[] }>('/simulations/readings', simulationId ? { simulation: simulationId } : undefined),
    enabled,
    staleTime: 60_000,
  });
}

export function useSavedScenarios(enabled: boolean) {
  return useQuery({
    queryKey: ['simulations', 'saved'],
    queryFn: () => api.get<{ scenarios: SavedScenario[] }>('/simulations/scenarios'),
    enabled,
  });
}

/** Every Playground write refreshes the list, and a new or deleted site refreshes the fleet. */
function useAct<V, R = unknown>(fn: (v: V) => Promise<R>, touchesSites = false) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      if (touchesSites) void qc.invalidateQueries({ queryKey: ['sites'] });
    },
  });
}

export function useCreateSimulation() {
  return useAct(
    (body: SimulatedSiteDetail & { preset: string; counts: Record<string, number>; historyDays: number }) =>
      api.post<{ simulation: { id: string; siteId: string } }>('/simulations', body),
    true,
  );
}

export function useSimulationSetup(id: string | null) {
  return useQuery({
    queryKey: [...KEY, 'setup', id],
    queryFn: async () => (await api.get<{ setup: SimulationSetup }>(`/simulations/${id}/setup`)).setup,
    enabled: id !== null,
    staleTime: 0,
  });
}

/** Save an edited setup: refused (409) while a scenario plays; added devices report from the
    takeover on, removed ones are cleared on the API's tick. */
export function useEditSetup() {
  return useAct(
    ({ id, ...body }: SimulatedSiteDetail & { id: string; counts: Record<string, number> }) =>
      api.put<{ edit: { added: string[]; removed: string[]; takesOverAt: string | null; diagram: 'cleared' | 'kept' | 'none' } }>(
        `/simulations/${id}/setup`,
        body,
      ),
    true,
  );
}

export function usePlayScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scenario }: { id: string; scenario: string }) =>
      api.post<void>(`/simulations/${id}/scenario`, { scenario }),
    /* Refetched at once and again a moment later, so the card says "playing …" straight away
       rather than on the next 5 s poll (2026-10-21). */
    onSuccess: async () => {
      await qc.refetchQueries({ queryKey: KEY });
      window.setTimeout(() => void qc.refetchQueries({ queryKey: KEY }), 1_500);
    },
  });
}

export function useBackToNormal() {
  return useAct((id: string) => api.post<void>(`/simulations/${id}/normal`));
}

export function usePauseSimulation() {
  return useAct((id: string) => api.post<void>(`/simulations/${id}/pause`));
}

export function useResumeSimulation() {
  return useAct((id: string) => api.post<void>(`/simulations/${id}/resume`));
}

export function useDeleteSimulation() {
  /* The simulation stops at once; its rows are purged a few series at a time in the background. */
  return useAct((id: string) => api.del<{ purging: true; series: number }>(`/simulations/${id}`), true);
}

export function useSaveScenario() {
  /* `simulation`: the site it was built for, whose devices (copies included) its steps may name. */
  return useAct((body: { name: string; definition: { steps: Step[] }; simulation?: string }) =>
    api.post<{ scenario: { id: string; seconds: number } }>('/simulations/scenarios', body),
  );
}

export function useDeleteSavedScenario() {
  return useAct((id: string) => api.del<void>(`/simulations/scenarios/${id}`));
}
